/**
 * A thin React wrapper around an ECharts chart, drawn as SVG. The option comes
 * from a pure builder (integrations/mdCharts.js), which is where the charts
 * are tested; this only creates, updates, resizes and disposes the chart.
 * `onChart` hands back the chart instance, which a report uses to export the
 * picture (`getDataURL`).
 */
import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';

import { loadEcharts } from './loadEcharts';

/**
 * ECharts measures text on a 2D canvas, which a DOM emulator such as jsdom
 * (the unit tests) does not have: charts are tested through their options.
 */
function canDraw() {
    return typeof window !== 'undefined'
        && !/jsdom/i.test(window.navigator?.userAgent || '');
}

export default function EChart({
    option, height = 320, ariaLabel, onChart,
}) {
    const hostRef = useRef(null);
    const chartRef = useRef(null);
    const optionRef = useRef(option);
    optionRef.current = option;
    const onChartRef = useRef(onChart);
    onChartRef.current = onChart;

    useEffect(() => {
        if (!canDraw()) return undefined;
        let disposed = false;
        let observer = null;
        loadEcharts().then((echarts) => {
            if (disposed || !hostRef.current) return;
            const chart = echarts.init(hostRef.current, null, { renderer: 'svg' });
            chartRef.current = chart;
            if (optionRef.current) chart.setOption(optionRef.current, true);
            if (typeof ResizeObserver !== 'undefined') {
                observer = new ResizeObserver(() => chart.resize());
                observer.observe(hostRef.current);
            }
            if (onChartRef.current) onChartRef.current(chart);
        }).catch((error) => console.warn('Could not load the chart library:', error));
        return () => {
            disposed = true;
            if (observer) observer.disconnect();
            if (chartRef.current) {
                if (onChartRef.current) onChartRef.current(null);
                chartRef.current.dispose();
                chartRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (chartRef.current && option) chartRef.current.setOption(option, true);
    }, [option]);

    return (
        <Box
            ref={hostRef}
            role="img"
            aria-label={ariaLabel}
            sx={{ width: '100%', height: `${height}px` }}
        />
    );
}
